import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

const ONBOARDING_STEPS = [
  {
    title: 'Templates & sections',
    body: 'Start from the left sidebar: choose a template, event scope, and group filter, then continue with your section setup.',
    highlightLabel: 'Sidebar: Templates',
    highlightZone: 'sidebar',
  },
  {
    title: 'Visual editor + preview',
    body: 'Drag sections, adjust spacing, and watch the live preview mirror your canvas in real time.',
    highlightLabel: 'Canvas + Preview',
    highlightZone: 'preview',
  },
  {
    title: 'Content, calendar & wallet',
    body: 'Use the Content, Calendar, Wallet, and Text Template panels to manage copy and interactions.',
    highlightLabel: 'Content panels',
    highlightZone: 'panels',
  },
  {
    title: 'Generate & export',
    body: 'Export responsive HTML, send a test email, or duplicate this template into another event.',
    highlightLabel: 'Generate center',
    highlightZone: 'generate',
  },
];

const arrowPositions = {
  sidebar: { top: '50%', right: '-10px', transform: 'translateY(-50%) rotate(-90deg)' },
  preview: { top: '-12px', right: '8px', transform: 'rotate(0deg)' },
  panels: { bottom: '-10px', left: '40%', transform: 'rotate(90deg)' },
  generate: { bottom: '-12px', right: '16px', transform: 'rotate(45deg)' },
};

const ArrowIndicator = ({ zone, prefersReducedMotion }) => {
  const style = {
    position: 'absolute',
    fontSize: '20px',
    color: 'var(--primary-color)',
    ...arrowPositions[zone],
  };
  const className = prefersReducedMotion ? '' : 'arrow-pulse';
  return (
    <span aria-hidden="true" className={className} style={style}>
      ➤
    </span>
  );
};

export const OnboardingOverlay = ({ onSkip, onComplete }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();
  const primaryButtonRef = useRef(null);
  const totalSteps = ONBOARDING_STEPS.length;
  const descriptionId = `onboarding-description-${stepIndex}`;
  const activeStep = ONBOARDING_STEPS[stepIndex];

  useEffect(() => {
    primaryButtonRef.current?.focus();
  }, [stepIndex]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onSkip();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSkip]);

  const stepCardStyle = useMemo(() => {
    if (prefersReducedMotion) {
      return { transition: 'none' };
    }
    return {
      transition: 'opacity 0.35s ease, transform 0.35s ease',
    };
  }, [prefersReducedMotion]);

  const handleNext = () => {
    if (stepIndex >= totalSteps - 1) {
      onComplete();
      return;
    }
    setStepIndex(prev => Math.min(prev + 1, totalSteps - 1));
  };

  const handleBack = () => {
    setStepIndex(prev => Math.max(prev - 1, 0));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur-sm"
      role="presentation"
    >
      <div
        className="relative w-full max-w-3xl rounded-3xl bg-white/95 p-6 shadow-2xl ring-1 ring-slate-900/10"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        aria-describedby={descriptionId}
      >
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              Guided tour
            </p>
            <h2
              id="onboarding-title"
              className="text-2xl font-bold text-slate-900"
            >
              Welcome aboard
            </h2>
          </div>
          <button
            type="button"
            onClick={onSkip}
            className="text-sm font-semibold text-slate-500 transition hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            Skip
          </button>
        </div>

        <div className="relative mt-6 h-64 overflow-hidden">
          {ONBOARDING_STEPS.map((step, index) => {
            const isActive = index === stepIndex;
            const offset = index - stepIndex;
            const transformStyle = prefersReducedMotion
              ? {}
              : { transform: `translateX(${offset * 110}%)` };
            return (
              <div
                key={step.title}
                aria-hidden={!isActive}
                style={{
                  ...stepCardStyle,
                  ...transformStyle,
                  opacity: isActive ? 1 : 0,
                  pointerEvents: isActive ? 'auto' : 'none',
                }}
                className="absolute inset-0 flex flex-col items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-white p-6 shadow-lg"
              >
                <div>
                  <p className="text-xs font-medium tracking-[0.3em] text-primary">
                    Step {index + 1} of {totalSteps}
                  </p>
                  <h3 className="mt-3 text-xl font-semibold text-slate-900">
                    {step.title}
                  </h3>
                  <p
                    id={isActive ? descriptionId : undefined}
                    className="mt-2 text-sm leading-relaxed text-slate-600"
                  >
                    {step.body}
                  </p>
                </div>
                {index === totalSteps - 1 && (
                  <p className="text-xs font-medium uppercase tracking-[0.3em] text-slate-400">
                    You can revisit this via your profile menu.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            <span>Focus</span>
            <span className="h-1 w-1 rounded-full bg-primary" />
            <span>{activeStep.highlightLabel}</span>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-900/5 p-4 shadow-inner">
            <div className="grid h-36 grid-cols-[110px_1fr] gap-3">
              <div
                className={`relative rounded-2xl border px-3 py-3 text-[11px] uppercase tracking-[0.2em] ${
                  activeStep.highlightZone === 'sidebar'
                    ? 'border-primary bg-primary/10 text-primary font-semibold'
                    : 'bg-white/80 border-slate-200 text-slate-500'
                }`}
              >
                Sidebar
                {activeStep.highlightZone === 'sidebar' && (
                  <ArrowIndicator
                    zone="sidebar"
                    prefersReducedMotion={prefersReducedMotion}
                  />
                )}
              </div>
              <div className="relative flex flex-col justify-between gap-2 rounded-2xl border bg-white/80 p-3 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                <div>Canvas</div>
                <div className="h-20 w-full rounded-xl bg-slate-100" />
                {activeStep.highlightZone === 'preview' && (
                  <ArrowIndicator
                    zone="preview"
                    prefersReducedMotion={prefersReducedMotion}
                  />
                )}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-[10px] uppercase tracking-[0.2em]">
              <div
                className={`relative rounded-2xl border p-3 ${
                  activeStep.highlightZone === 'panels'
                    ? 'border-primary bg-primary/10 text-primary font-semibold'
                    : 'border-slate-200 bg-white/80 text-slate-500'
                }`}
              >
                Content panels
                {activeStep.highlightZone === 'panels' && (
                  <ArrowIndicator
                    zone="panels"
                    prefersReducedMotion={prefersReducedMotion}
                  />
                )}
              </div>
              <div
                className={`relative rounded-2xl border p-3 ${
                  activeStep.highlightZone === 'generate'
                    ? 'border-primary bg-primary/10 text-primary font-semibold'
                    : 'border-slate-200 bg-white/80 text-slate-500'
                }`}
              >
                Generate & export
                {activeStep.highlightZone === 'generate' && (
                  <ArrowIndicator
                    zone="generate"
                    prefersReducedMotion={prefersReducedMotion}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            <span>Progress</span>
            <span className="h-1 w-1 rounded-full bg-primary" />
            <span>
              {stepIndex + 1} / {totalSteps}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              disabled={stepIndex === 0}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Back
            </button>
            <button
              ref={primaryButtonRef}
              type="button"
              onClick={handleNext}
              className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80 active:scale-[0.98]"
            >
              {stepIndex === totalSteps - 1 ? 'Launch app' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
